<template>
  <CenteredPageContainer :class="$style.page">
    <BreadcrumbList :links="breadcrumbs" />
    <section :class="[$style.page__section, $style.section]">
      <SectionTitle :class="$style.section__title">お知らせ一覧</SectionTitle>
      <section
        :class="[$style.section__body, $style.body, $style['body--smmary']]"
      >
        <FadeTransition>
          <div :class="$style.body__message" v-if="loading">
            <LoadingIndicator />
          </div>
          <AdminNewsList :articles="[...articles]" v-else />
        </FadeTransition>
      </section>

      <FlatButton
        @click="fetchMore"
        v-if="hasNextPage"
        :class="$style.button__loadmore"
        variant="primary"
        size="medium"
      >
        もっと読み込む
      </FlatButton>
    </section>
  </CenteredPageContainer>
</template>

<script lang="ts" setup>
import { ref, computed, watch } from 'vue'
import FadeTransition from '@/components/transitions/FadeTransition.vue'
import LoadingIndicator from '@/components/atoms/LoadingIndicator.vue'
import CenteredPageContainer from '@/components/molecules/CenteredPageContainer.vue'
import SectionTitle from '@/components/atoms/SectionTitle.vue'
import BreadcrumbList from '@/components/molecules/BreadcrumbList.vue'
import AdminNewsList from '@/features/adminNews/components/AdminNewsList/AdminNewsList.vue'
import type { BreadCrumbLink } from '@/types/app'
import FlatButton from '@/components/atoms/FlatButton.vue'
import { useHead } from '@unhead/vue'
import { useRoute } from 'vue-router'
import {
  useAdminNewsListPageQuery,
  type AdminNewsListPageQuery,
} from './AdminNewsListPage.generated'

const articles = ref<
  AdminNewsListPageQuery['newsForPlanner']['edges'][number]['node'][]
>([])
const cursor = ref<string | null>(null)
const first = 10

const {
  result,
  fetchMore: _fetchMore,
  loading,
} = useAdminNewsListPageQuery({
  first,
})

watch(
  () => result.value,
  value => {
    if (value) {
      cursor.value = value.newsForPlanner.pageInfo.hasNextPage
        ? value.newsForPlanner.pageInfo.endCursor || null
        : null

      articles.value = [
        ...articles.value,
        ...value.newsForPlanner.edges.map(edge => edge.node),
      ]
    }
  },
  { immediate: true }
)

const hasNextPage = computed(() => {
  return !!cursor.value
})

const fetchMore = async () => {
  const _result = await _fetchMore({
    variables: {
      first,
      after: cursor.value,
    },
  })
  if (!_result) {
    return
  }

  result.value = _result.data
}

const route = useRoute('news')
const breadcrumbs = computed(
  (): ReadonlyArray<BreadCrumbLink> => route.meta.breadcrumbs ?? []
)

useHead({
  title: 'お知らせ',
})
</script>

<style lang="scss" module>
.page {
  &__section {
    &:nth-child(n + 2) {
      margin-top: 32px;
    }
  }
}

.section {
  &__title {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }

  &__body {
    &:nth-child(n + 2) {
      margin-top: 24px;
    }
  }
}

.button {
  &__loadmore {
    display: block;
    margin-top: 2em;
    margin-right: auto;
    margin-left: auto;
  }
}

.body {
  background-color: $color-surface-primary;
  border-radius: $border-radius-v2-rounder;
  box-shadow: $shadow-24;

  &--summary {
    padding: 24px;
  }

  &__message {
    display: flex;
    min-height: 40px;
    align-items: center;
    justify-content: center;
  }
}
</style>
